import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import type { TaskItemReaction } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import { createReactionState } from '../../reactions/reactionActions';
import { deselectElementChip } from '../elementChipsActions';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';
import type { ElementChipCallbackSet } from './useElementChipCallbacks';

/**
 * Reaction callbacks for task-item chips. Pressing a reaction toggles the current
 * user's row in the node's `reactions` attr, reusing the element `createReactionState`
 * toggle logic so node-attr reactions stay consistent with element reactions.
 */
export const useReactionTaskItemChipCallbacks = ({
    reactions,
    currentUserId,
    updateAttributes,
    canModifyReactions,
}: {
    reactions: TaskItemReaction[];
    currentUserId: string | undefined;
    updateAttributes: UpdateTaskItemAttrs;
    canModifyReactions: boolean;
}): ElementChipCallbackSet => {
    const dispatch = useDispatch();

    return useMemo(
        () => (chip) => {
            if (chip.type !== 'reaction') return {};

            return {
                canModify: canModifyReactions,
                onPressImmediate: () => {
                    if (!canModifyReactions || !currentUserId) return;

                    dispatch(deselectElementChip());
                    updateAttributes({
                        reactions: createReactionState({
                            existingReactionState: reactions,
                            emojiCode: chip.emojiCode,
                            userId: currentUserId,
                        }),
                    });
                },
            };
        },
        [canModifyReactions, currentUserId, dispatch, reactions, updateAttributes],
    );
};
